// src/services/diagnosticScans.js
//
// The scans of a patient with the Diagnostics diagnosis: the rules behind the
// patient page's "Diagnostics Clinical Data" section. Firebase-free, so it can be
// tested on its own.
//
// Stored as `diagnosticScans` on the patient record: on the Firestore doc, or
// inside CLINICAL_DATA for a Google Sheet patient. Written only once a patient
// has a scan, so a save for any other patient adds nothing to their record.
// A scan is never removed from the list. The server keeps the same promise for
// Sheet patients (clinicalForPatientSave in functions/sheetEnrollment.js).

export const SCAN_TYPES = ["LDCT", "PET", "CT Thorax", "CT Angio"];
export const NODULE_CHANGES = ["New", "Stable", "Increased", "Decreased"];

const newScanId = () => `scan_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;

export const blankScan = (id = newScanId()) => ({
  id,
  date: "",
  type: "",
  nodulesPresent: "",
  size: "",
  change: "",
  spiculated: false,
  priorSize: "",
});

const isBlankScan = (scan) =>
  !scan.date &&
  !scan.type &&
  !scan.nodulesPresent &&
  !scan.size &&
  !scan.change &&
  !scan.spiculated &&
  !scan.priorSize;

// The stored list, with any missing field filled in. Fields this code doesn't
// know about are kept.
export const normalizeScans = (raw) =>
  (Array.isArray(raw) ? raw : [])
    .filter((s) => s && typeof s === "object" && s.id)
    .map((s) => ({ ...blankScan(s.id), ...s }));

// What the section starts with: the saved scans, or one empty block to fill in.
export const scansForEditing = (raw) => {
  const list = normalizeScans(raw);
  return list.length ? list : [blankScan()];
};

// The list to save: the stored scans, updated from the page, then the page's new
// ones. Every stored scan stays, including one only another person's tab knows
// about. An empty block that was never saved is dropped.
export const scansForSave = (stored, local) => {
  const storedList = normalizeScans(stored);
  const storedIds = new Set(storedList.map((s) => s.id));
  const localList = normalizeScans(local);
  const localById = new Map(localList.map((s) => [s.id, s]));
  return [
    ...storedList.map((s) => (localById.has(s.id) ? { ...s, ...localById.get(s.id) } : s)),
    ...localList.filter((s) => !storedIds.has(s.id) && !isBlankScan(s)),
  ];
};

// The field to add to a save: nothing for a patient who has no scans and never had.
export const scanFieldsForSave = (stored, local) => {
  const diagnosticScans = scansForSave(stored, local);
  return diagnosticScans.length || (stored !== undefined && stored !== null) ? { diagnosticScans } : {};
};

// Hiding a field never clears it: these only decide what is on screen.
export const showNoduleDetails = (scan) => scan.nodulesPresent === "Yes";
export const showPriorSize = (scan) => showNoduleDetails(scan) && !!scan.change && scan.change !== "Stable";
